import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, UnfoldMore as SortIcon } from '@mui/icons-material';

// Column definition type
export interface Column<T> {
  /** Unique key for the column */
  key: string;
  /** Header text displayed in the table header */
  header: string;
  /** Accessor function to get the value from the row data */
  accessor?: (row: T) => React.ReactNode;
  /** Custom render function for complex cell content */
  render?: (row: T, index: number) => React.ReactNode;
  /** Minimum width of the column */
  minWidth?: number;
  /** Maximum width of the column */
  maxWidth?: number;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Whether to prevent text wrapping */
  noWrap?: boolean;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Returns the primitive value used for sorting; falls back to accessor result */
  sortValue?: (row: T) => string | number | null | undefined;
  /** Custom header cell renderer — replaces the header string */
  headerRender?: () => React.ReactNode;
}

// Props for the DataTable component
export interface DataTableProps<T> {
  /** Array of column definitions */
  columns: Column<T>[];
  /** Array of data to display */
  data: T[];
  /** Unique key accessor for each row */
  rowKey: (row: T) => string | number;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Message to show when no data is available */
  emptyMessage?: string;
  /** Secondary message for empty state */
  emptySubMessage?: string;
  /** Loading message */
  loadingMessage?: string;
  /** Callback when a row is clicked */
  onRowClick?: (row: T) => void;
  /** Callback for retry button when error occurs */
  onRetry?: () => void;
  /** Rows per page options */
  rowsPerPageOptions?: number[];
  /** Default rows per page */
  defaultRowsPerPage?: number;
  /** Whether to show pagination */
  showPagination?: boolean;
  /** Minimum table width */
  minTableWidth?: number;
  /** Custom sx props for the table container Paper */
  paperSx?: object;
  /** Key of the currently active/selected row (must match rowKey output) */
  activeRowKey?: string | number | null;
  /** Callback when a row is right-clicked (context menu) */
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void;
  /** Minimum height (px) for each data row */
  rowHeight?: number;
}

type SortDirection = 'asc' | 'desc';

function DataTable<T>({
  columns,
  data,
  rowKey,
  isLoading = false,
  error = null,
  emptyMessage = 'No data found',
  emptySubMessage,
  loadingMessage = 'Loading...',
  onRowClick,
  onRetry,
  rowsPerPageOptions = [5, 10, 25],
  defaultRowsPerPage = 10,
  showPagination = true,
  minTableWidth = 650,
  paperSx = {},
  activeRowKey = null,
  onRowContextMenu,
  rowHeight,
}: DataTableProps<T>) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleHeaderClick = (column: Column<T>) => {
    if (!column.sortable) return;
    if (sortKey === column.key) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(column.key);
      setSortDirection('asc');
    }
    setPage(0);
  };

  // Reset page when data shrinks below the current page
  React.useEffect(() => {
    if (page > 0 && data && page * rowsPerPage >= data.length) {
      setPage(Math.max(0, Math.ceil(data.length / rowsPerPage) - 1));
    }
  }, [data.length, rowsPerPage]); // intentionally exclude `page` to avoid loops

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const column = columns.find(c => c.key === sortKey);
    if (!column) return data;

    return [...data].sort((a, b) => {
      const aVal = column.sortValue ? column.sortValue(a) : null;
      const bVal = column.sortValue ? column.sortValue(b) : null;

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const result = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? result : -result;
    });
  }, [data, sortKey, sortDirection, columns]);

  // Scrollbar styles - use overlay to prevent layout shift on hover
  const scrollbarStyles = {
    overflowX: 'auto',
    // Prevent scrollbar from causing layout shifts
    scrollbarGutter: 'stable',
    paddingBottom: 2,
    '&::-webkit-scrollbar': {
      height: 6,
      background: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.text.disabled,
      borderRadius: 3,
      '&:hover': {
        backgroundColor: theme.palette.text.secondary,
      },
    },
  };

  // Pagination styles
  const paginationStyles = {
    color: theme.palette.text.primary,
    '& .MuiTablePagination-toolbar': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-select': {
      color: theme.palette.text.primary,
    },
    '& .MuiTablePagination-selectIcon': {
      color: theme.palette.text.secondary,
    },
    '& .MuiTablePagination-actions .MuiIconButton-root': {
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.interactive?.hover || theme.palette.interactive.hover,
      },
      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 8,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            sx={{
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {loadingMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          //border: 'none'
        }}
      >
        <Typography sx={{ color: theme.palette.error.main, mb: 2 }}>
          {error}
        </Typography>
        {onRetry && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            sx={{
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              '&:hover': {
                backgroundColor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
              },
            }}
          >
            Retry
          </Button>
        )}
      </Paper>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>
          {emptyMessage}
        </Typography>
        {emptySubMessage && (
          <Typography sx={{ color: theme.palette.text.secondary }}>
            {emptySubMessage}
          </Typography>
        )}
      </Paper>
    );
  }

  // Data table
  const paginatedData = showPagination
    ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedData;

  return (
    <Paper
      sx={{
        backgroundColor: 'transparent',
        overflow: 'hidden',
        boxShadow: 'none',
        border: 'none',
        ...paperSx,
      }}
    >
      <TableContainer sx={scrollbarStyles}>
        <Table sx={{ minWidth: minTableWidth, width: '100%' }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const isActive = sortKey === column.key;
                return (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    onClick={() => handleHeaderClick(column)}
                    sx={{
                      color: isActive ? theme.palette.text.primary : theme.palette.text.tertiary,
                      fontWeight: 600,
                      fontSize: '0.70rem',
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      padding: '10px 16px',
                      borderBottom: `1px solid ${theme.palette.border.main}`,
                      backgroundColor: theme.palette.background.secondary,
                      whiteSpace: 'nowrap',
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      cursor: column.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      '&:hover': column.sortable ? {
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.elevated,
                      } : {},
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start' }}>
                      {column.headerRender ? column.headerRender() : column.header}
                      {column.sortable && (
                        isActive
                          ? sortDirection === 'asc'
                            ? <ArrowUpIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                            : <ArrowDownIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                          : <SortIcon sx={{ fontSize: 14, opacity: 0.3 }} />
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => {
              const isActive = activeRowKey != null && rowKey(row) === activeRowKey;
              return (
              <TableRow
                key={rowKey(row)}
                onClick={() => onRowClick?.(row)}
                onContextMenu={(e) => {
                  if (onRowContextMenu) {
                    e.preventDefault();
                    onRowContextMenu(row, e);
                  }
                }}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background-color 0.15s ease',
                  backgroundColor: isActive
                    ? theme.palette.interactive.selected
                    : 'transparent',
                  boxShadow: isActive
                    ? `inset 3px 0 0 0 ${theme.palette.border.strong}`
                    : 'none',
                  '&:hover': {
                    backgroundColor: isActive
                      ? theme.palette.interactive.selected
                      : theme.palette.background.elevated,
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align || 'left'}
                    sx={{
                      color: theme.palette.text.primary,
                      padding: '10px 16px',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      whiteSpace: column.noWrap ? 'nowrap' : 'normal',
                      ...(rowHeight ? { height: rowHeight } : {}),
                    }}
                  >
                    {column.render
                      ? column.render(row, index)
                      : column.accessor
                      ? column.accessor(row)
                      : null}
                  </TableCell>
                ))}
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <Box
          sx={{
            backgroundColor: 'transparent',
            pr: 2,
          }}
        >
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={paginationStyles}
          />
        </Box>
      )}
    </Paper>
  );
}

export default DataTable;
